import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Copy, Check, Trophy, Instagram, Music2, Twitter, Youtube, Lock } from "lucide-react";
import { authAxios } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "tiktok", label: "TikTok", icon: Music2 },
  { key: "twitter", label: "X / Twitter", icon: Twitter },
  { key: "youtube", label: "YouTube", icon: Youtube },
];

// ─────── "I signed up!" proof modal ───────
export const SignedUpModal = ({ open, onClose, program, onSubmitted }) => {
  const [proofUrl, setProofUrl] = useState("");
  const [proofNote, setProofNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!proofUrl.trim() && !proofNote.trim()) {
      toast.error("Add a screenshot URL or a short note as proof");
      return;
    }
    setBusy(true);
    try {
      await authAxios.post("/user/affiliate-signups", {
        program_slug: program.slug,
        proof_url: proofUrl.trim() || null,
        proof_note: proofNote.trim() || null,
      });
      toast.success(`Submitted for ${program.name}! Admin approves within 24h.`);
      onSubmitted?.();
      onClose();
      setProofUrl(""); setProofNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  if (!program) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="signed-up-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-emerald-600" /> I signed up to {program.name}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 mb-3">
          Submit proof (screenshot link or short note) — once admin approves, it counts toward the monthly leaderboard and unlocks prize eligibility.
        </p>
        <input
          data-testid="proof-url-input"
          value={proofUrl}
          onChange={(e) => setProofUrl(e.target.value)}
          placeholder="Screenshot URL (Imgur, Drive, etc.) — optional"
          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm mb-3"
        />
        <textarea
          data-testid="proof-note-input"
          value={proofNote}
          onChange={(e) => setProofNote(e.target.value)}
          placeholder="Or leave a short note (e.g., 'signed up via CJ, publisher ID 1234567')"
          rows={3}
          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm mb-4 resize-none"
        />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:border-slate-400">Cancel</button>
          <button data-testid="proof-submit-button" onClick={submit} disabled={busy} className="flex-1 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-500 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Submit for review"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─────── AI post generator modal (paid only) ───────
export const AiPostModal = ({ open, onClose, program, isPaid }) => {
  const [platform, setPlatform] = useState("instagram");
  const [tone, setTone] = useState("genuine and enthusiastic");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [quota, setQuota] = useState(null);

  const generate = async () => {
    setBusy(true); setCaption(""); setCopied(false);
    try {
      const r = await authAxios.post("/user/ai/generate-post", { program_slug: program.slug, platform, tone });
      setCaption(r.data.caption);
      setQuota({ used: r.data.used_today, limit: r.data.daily_limit });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(caption);
    setCopied(true);
    toast.success("Copied — paste it into your post");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!program) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-post-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> AI social post — {program.name}</DialogTitle>
        </DialogHeader>

        {!isPaid ? (
          <div data-testid="ai-paywall" className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-6 text-center">
            <Lock className="w-8 h-8 text-amber-600 mx-auto mb-3" />
            <h4 className="font-bold text-slate-900 mb-1">Members Club only</h4>
            <p className="text-sm text-slate-600 mb-4">Unlock AI-written affiliate posts for just $1.99/month. 20 fresh captions every day, ready to copy-paste.</p>
            <Link
              to="/membership"
              data-testid="ai-upgrade-cta"
              onClick={onClose}
              className="inline-block px-6 py-2.5 rounded-full bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-500"
            >
              Upgrade for $1.99/mo →
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                const active = platform === p.key;
                return (
                  <button
                    key={p.key}
                    data-testid={`ai-platform-${p.key}`}
                    onClick={() => setPlatform(p.key)}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-all ${active ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                  >
                    <Icon className="w-4 h-4" />
                    {p.label}
                  </button>
                );
              })}
            </div>

            <input
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              placeholder="Tone (e.g., funny, professional, Gen-Z)"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-3"
              data-testid="ai-tone-input"
            />

            <button
              data-testid="ai-generate-button"
              onClick={generate}
              disabled={busy}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:opacity-90 disabled:opacity-50 mb-3"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Writing…</> : <>{caption ? "✨ Regenerate" : "✨ Generate post"}</>}
            </button>

            {caption && (
              <div data-testid="ai-caption-output" className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-3">
                <p className="text-sm text-slate-900 whitespace-pre-wrap leading-relaxed">{caption}</p>
                <button
                  data-testid="ai-copy-button"
                  onClick={copy}
                  className="mt-3 w-full py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 inline-flex items-center justify-center gap-2"
                >
                  {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy caption</>}
                </button>
              </div>
            )}

            {quota && (
              <p className="text-xs text-center text-slate-500">{quota.used} / {quota.limit} posts used today</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
